/**
 * Licenças — o catálogo de **planos**, não as licenças de ninguém.
 *
 * A licença diz quanto vale um plano: quantos dias, quantas visualizações, o
 * que alcança. Quem assina é o *contrato*, e é ele que a ficha do cliente chama
 * de "licença dele". Aqui não há dado pessoal nenhum — é a única peça deste
 * módulo que dá para mexer à vontade.
 *
 * **Duas armadilhas de vocabulário**, ambas do banco:
 *
 *   `licenca.validade`   é um **número de dias**
 *   `contrato.validade`  é uma **data**
 *
 * e a cota `0` significa **ilimitado**, que não é o mesmo que o `1000000` que as
 * licenças anuais gravam — finito no papel, infinito na prática.
 *
 * **O alcance é o que mais importa e o que menos aparece.** Duas tabelas de
 * vínculo decidem se o plano de linha leve abre um caminhão, e é isso que
 * `PermiteAcesso` confere quando o cliente abre um conteúdo. Aqui elas são
 * campo de primeira classe.
 *
 * Dado global — a trava recusa gravação em desenvolvimento.
 */

import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import {
  useExcluirLicenca, useLicencas, useSalvarLicenca,
  type Licenca, type SalvarLicenca,
} from "@/api/clientes"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import {
  Aviso, Botao, Campo, ColunaDeLista, ItemDaLista, Linha, Painel, Secao,
  Vazio, estiloCampo, mensagem,
} from "../outros/pecas"

type Referencia = { id: number; categoria?: string; titulo?: string }

const EM_BRANCO: SalvarLicenca = {
  titulo: "",
  classificacao: "",
  validade: 365,
  visualizacoes: 0,
  atualizacao: 365,
  conteudo: true,
  sistema: false,
  diagIA: false,
  teste: false,
  categoriaVeiculoId: 0,
  categoriasDeVeiculo: [],
  tiposDeInformacao: [],
}

export function Licencas() {
  const [aberta, setAberta] = useState<number | null>(null)
  const [criando, setCriando] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const licencas = useLicencas()

  const categorias = useQuery({
    queryKey: ["categoria-veiculos"],
    queryFn: () => obter<Referencia[]>("api/categoria-veiculos"),
    refetchOnWindowFocus: false,
  })
  const tipos = useQuery({
    queryKey: ["tipo-informacoes"],
    queryFn: () => obter<Referencia[]>("api/tipo-informacoes"),
    refetchOnWindowFocus: false,
  })

  const escolhida = useMemo(
    () => (licencas.data ?? []).find((l) => l.id === aberta) ?? null,
    [licencas.data, aberta],
  )

  const emUso = (licencas.data ?? []).reduce((s, l) => s + l.contratosVigentes, 0)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Licenças"
        detalhe={licencas.data ? `${licencas.data.length} planos · ${emUso.toLocaleString("pt-BR")} contratos vigentes` : undefined}
      >
        <Botao destaque aoClicar={() => { setCriando(true); setAberta(null); setRecado(null) }}>
          Nova licença
        </Botao>
      </Faixa>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <ColunaDeLista largura={340}>
          {licencas.isLoading ? (
            <Vazio>Lendo o catálogo…</Vazio>
          ) : licencas.isError ? (
            <Vazio>{mensagem(licencas.error)}</Vazio>
          ) : (
            (licencas.data ?? []).map((l) => (
              <ItemDaLista
                key={l.id}
                ativo={aberta === l.id && !criando}
                aoClicar={() => { setAberta(l.id); setCriando(false); setRecado(null) }}
                marcado={l.contratosVigentes > 0}
                titulo={l.titulo}
                detalhe={
                  <span style={{ fontFamily: "var(--fm)", fontSize: 10.5 }}>
                    {l.validadeTexto}
                    {l.semLimite ? " · sem limite" : ` · ${l.visualizacoes.toLocaleString("pt-BR")} vis.`}
                    {l.contratosVigentes > 0 && ` · ${l.contratosVigentes.toLocaleString("pt-BR")} em uso`}
                  </span>
                }
              />
            ))
          )}
        </ColunaDeLista>

        <Painel>
          {criando ? (
            <Formulario
              chave="nova"
              inicial={EM_BRANCO}
              id={null}
              categorias={categorias.data ?? []}
              tipos={tipos.data ?? []}
              aoAvisar={(m) => { setRecado(m); setCriando(false) }}
              aoCancelar={() => setCriando(false)}
            />
          ) : escolhida ? (
            <Formulario
              chave={String(escolhida.id)}
              id={escolhida.id}
              inicial={paraFormulario(escolhida)}
              emUso={escolhida.contratosVigentes}
              categorias={categorias.data ?? []}
              tipos={tipos.data ?? []}
              aoAvisar={setRecado}
            />
          ) : (
            <Vazio>
              Escolha um plano à esquerda, ou crie um novo. São 32 em produção, de "30 dias ou
              60 visualizações" a "Diagweb Vitalício".
            </Vazio>
          )}
        </Painel>
      </div>

      <BarraDeEstado direita={recado}>
        {licencas.data
          ? `${licencas.data.length} planos · ${emUso.toLocaleString("pt-BR")} contratos vigentes dependem deles`
          : "—"}
      </BarraDeEstado>
    </div>
  )
}

function paraFormulario(l: Licenca): SalvarLicenca {
  return {
    titulo: l.titulo,
    classificacao: l.classificacao,
    validade: l.validadeEmDias,
    visualizacoes: l.visualizacoes,
    atualizacao: l.atualizacaoEmDias,
    conteudo: l.conteudo,
    sistema: l.sistema,
    diagIA: l.diagIA,
    teste: l.teste,
    categoriaVeiculoId: 0,
    categoriasDeVeiculo: l.categoriasDeVeiculo.map((c) => c.id),
    tiposDeInformacao: l.tiposDeInformacao.map((t) => t.id),
  }
}

function Formulario({ chave, id, inicial, emUso = 0, categorias, tipos, aoAvisar, aoCancelar }: {
  chave: string
  id: number | null
  inicial: SalvarLicenca
  emUso?: number
  categorias: Referencia[]
  tipos: Referencia[]
  aoAvisar: (m: string) => void
  aoCancelar?: () => void
}) {
  const [f, setF] = useState<SalvarLicenca>(inicial)
  const [erro, setErro] = useState<string | null>(null)

  // Trocar de licença na lista tem de trocar o formulário inteiro; sem isto os
  // campos ficariam com o valor da anterior.
  useEffect(() => { setF(inicial); setErro(null) }, [chave])

  const salvar = useSalvarLicenca()
  const excluir = useExcluirLicenca()

  const muda = <K extends keyof SalvarLicenca>(k: K, v: SalvarLicenca[K]) =>
    setF((a) => ({ ...a, [k]: v }))

  // A mesma normalização que o LicencaController do Painel faz, mostrada antes
  // de gravar: atualização zerada ou maior que a validade vira a validade.
  const corteEfetivo = f.atualizacao <= 0 || f.atualizacao > f.validade ? f.validade : f.atualizacao

  async function gravar() {
    setErro(null)
    try {
      const r = await salvar.mutateAsync({ id, dados: f })
      aoAvisar(`${id == null ? "Criada" : "Alterada"}: ${r.titulo}`)
    } catch (e) {
      setErro(mensagem(e))
    }
  }

  async function remover() {
    setErro(null)
    try {
      await excluir.mutateAsync(id!)
      aoAvisar("Licença excluída.")
    } catch (e) {
      setErro(mensagem(e))
    }
  }

  return (
    <>
      <AvisoDeDadoGlobal oQueMuda="planos que valem para todos os clientes, em produção" />

      <Secao titulo={id == null ? "Nova licença" : `Licença ${id}`} contagem={emUso > 0 ? `${emUso.toLocaleString("pt-BR")} contratos vigentes` : undefined}>
        <Linha>
          <Campo rotulo="Título">
            <input value={f.titulo} onChange={(e) => muda("titulo", e.target.value)} style={estiloCampo} />
          </Campo>
          <Campo rotulo="Classificação" ajuda="Rótulo curto usado na venda: 12m, 50v30d.">
            <input value={f.classificacao ?? ""} onChange={(e) => muda("classificacao", e.target.value)} style={{ ...estiloCampo, width: 110 }} />
          </Campo>
        </Linha>

        <Linha>
          <Campo rotulo="Validade (dias)" ajuda="Aqui é número de dias. No contrato, validade é data.">
            <input type="number" value={f.validade} onChange={(e) => muda("validade", Number(e.target.value))} style={estiloCampo} />
          </Campo>
          <Campo rotulo="Visualizações" ajuda="0 significa ilimitado — é o que a regra de acesso lê.">
            <input type="number" value={f.visualizacoes} onChange={(e) => muda("visualizacoes", Number(e.target.value))} style={estiloCampo} />
          </Campo>
          <Campo rotulo="Atualização (dias)" ajuda="O corte: até onde o cliente enxerga conteúdo novo.">
            <input type="number" value={f.atualizacao} onChange={(e) => muda("atualizacao", Number(e.target.value))} style={estiloCampo} />
          </Campo>
        </Linha>

        {corteEfetivo !== f.atualizacao && (
          <Aviso>
            Atualização de {f.atualizacao} vai ser gravada como <b>{corteEfetivo}</b> — zerada ou
            maior que a validade, ela é normalizada para a própria validade. É a regra do
            <code> LicencaController</code> do Painel: sem ela, "vitalício com corte de 0 dias"
            significaria "não vê nada".
          </Aviso>
        )}
        {f.visualizacoes === 0 && (
          <Aviso>Cota <b>0 é ilimitado</b>, e não "nenhuma visualização".</Aviso>
        )}

        <Linha>
          {([["conteudo", "Libera conteúdo"], ["sistema", "Libera sistema"],
             ["diagIA", "Libera DiagIA"], ["teste", "É demonstração"]] as const).map(([k, r]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
              <input type="checkbox" checked={f[k] as boolean} onChange={(e) => muda(k, e.target.checked)} />
              {r}
            </label>
          ))}
        </Linha>
      </Secao>

      <Secao titulo="Alcance" contagem={`${f.categoriasDeVeiculo.length} categorias · ${f.tiposDeInformacao.length} tipos`}>
        <Aviso>
          É isto que decide se a licença de linha leve abre um caminhão. A regra de acesso
          confere os dois vínculos quando o cliente abre um conteúdo — <b>alcance vazio não
          abre nada</b> pela via do contrato.
        </Aviso>
        <Linha>
          <Multi
            rotulo="Categorias de veículo"
            opcoes={categorias.map((c) => ({ id: c.id, nome: c.categoria ?? `Categoria ${c.id}` }))}
            escolhidos={f.categoriasDeVeiculo}
            aoMudar={(v) => muda("categoriasDeVeiculo", v)}
          />
          <Multi
            rotulo="Tipos de informação"
            opcoes={tipos.map((t) => ({ id: t.id, nome: t.titulo ?? `Tipo ${t.id}` }))}
            escolhidos={f.tiposDeInformacao}
            aoMudar={(v) => muda("tiposDeInformacao", v)}
          />
        </Linha>
      </Secao>

      {erro && <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--erro-on-soft, #8a1f14)" }}>{erro}</p>}

      <Linha>
        <Botao destaque aoClicar={gravar} ocupado={salvar.isPending} desligado={!f.titulo.trim()}>
          {id == null ? "Criar" : "Gravar"}
        </Botao>
        {aoCancelar && <Botao aoClicar={aoCancelar}>Cancelar</Botao>}
        {id != null && (
          <Botao
            aoClicar={remover}
            ocupado={excluir.isPending}
            desligado={emUso > 0}
            titulo={emUso > 0
              ? `${emUso} contratos vigentes apontam para esta licença. Excluí-la faria esses clientes perderem o acesso em silêncio.`
              : undefined}
          >
            Excluir
          </Botao>
        )}
      </Linha>
    </>
  )
}

/** Marcação múltipla simples — o alcance são 25 e 36 linhas, cabe tudo na tela. */
function Multi({ rotulo, opcoes, escolhidos, aoMudar }: {
  rotulo: string
  opcoes: { id: number; nome: string }[]
  escolhidos: number[]
  aoMudar: (v: number[]) => void
}) {
  const marcados = new Set(escolhidos)

  return (
    <div style={{ flex: 1, minWidth: 220, marginBottom: 9 }}>
      <label style={{ display: "block", fontSize: 11, color: "var(--txt2)", marginBottom: 4 }}>
        {rotulo}
      </label>
      <div style={{
        border: "1px solid var(--line)", borderRadius: 5, padding: 6,
        maxHeight: 170, overflowY: "auto",
      }}>
        {opcoes.length === 0 ? (
          <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Sem opções.</span>
        ) : opcoes.map((o) => (
          <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0" }}>
            <input
              type="checkbox"
              checked={marcados.has(o.id)}
              onChange={(e) => aoMudar(e.target.checked
                ? [...escolhidos, o.id]
                : escolhidos.filter((x) => x !== o.id))}
            />
            {o.nome}
          </label>
        ))}
      </div>
    </div>
  )
}
